import {
  AlertCircle,
  Brain,
  CheckCircle,
  Lightbulb,
  GraduationCap,
  RefreshCw,
  Target,
  BookOpen,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import { Button, Text } from "@core/index";
import MisconceptionCardPDF from "./MisconceptionCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetCommonMisconceptionQueryKey,
  getGetUserCommonMisconceptionsQueryKey,
  updateCommonMisconception,
} from "@packages/api/madlenAPI";
import {
  CommonMisconceptionResponseDto,
  MisconceptionItem,
  CommonMisconceptionUpdateRequestDto,
} from "@packages/api/madlenAPI.schemas";

interface MisconceptionCardProps {
  misconception: CommonMisconceptionResponseDto;
  isEditable?: boolean;
  onCreateAgain?: () => void;
}

const MisconceptionCard = ({
  misconception,
  onCreateAgain,
  isEditable = false,
}: MisconceptionCardProps) => {
  const finalImageUrl = useImagePdf(misconception.imageUrl);
  const contentLanguage = misconception.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  // Helper function to update assessment strategies
  const updateAssessmentStrategies = async (
    field: "detection_methods" | "re_evaluation_methods",
    newMethods: string[],
  ) => {
    if (!misconception.assessmentStrategies) return;

    const updatedAssessmentStrategies = {
      ...misconception.assessmentStrategies,
      [field]: newMethods,
    };

    const updateData: CommonMisconceptionUpdateRequestDto = {
      assessmentStrategies: updatedAssessmentStrategies,
    };

    await updateCommonMisconception(misconception.id!, updateData);

    await queryClient.invalidateQueries({
      queryKey: getGetUserCommonMisconceptionsQueryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: getGetCommonMisconceptionQueryKey(misconception.id!),
    });
  };

  return (
    <div data-testid="misconception-card" className={"min-h-screen"}>
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {misconception.imageUrl && (
          <CardImage
            createdAt={misconception.createdAt}
            imageUrl={misconception.imageUrl}
            videoUrl={misconception.videoUrl}
            courseName={misconception.courseName}
            gradeLevel={misconception.gradeLevel}
            topic={misconception.title || misconception.prompt}
            theme="red"
            contentLanguage={contentLanguage}
          />
        )}
        <div className="flex justify-end mb-3 gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            document={
              <MisconceptionCardPDF
                customizationSettings={customizationSettings}
                misconception={{ ...misconception, imageUrl: finalImageUrl }}
              />
            }
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            btnTheme="bg-gradient-to-r from-red-500 to-red-600"
            pdfTitle={misconception.title || misconception.prompt}
          />
        </div>

        <div className="space-y-8">
          {misconception.assessmentStrategies &&
            ((misconception.assessmentStrategies.detection_methods &&
              misconception.assessmentStrategies.detection_methods.length >
                0) ||
              (misconception.assessmentStrategies.re_evaluation_methods &&
                misconception.assessmentStrategies.re_evaluation_methods
                  .length > 0)) && (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                    <Target size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {tC(contentLanguage, "misconceptions.assessmentStrategies")}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Detection Methods */}
                  {misconception.assessmentStrategies.detection_methods &&
                    misconception.assessmentStrategies.detection_methods
                      .length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <AlertCircle
                            size={20}
                            className="text-orange-600 dark:text-orange-400"
                          />
                          <h4 className="font-semibold text-orange-600 dark:text-orange-400">
                            {tC(
                              contentLanguage,
                              "misconceptions.detectionMethods",
                            )}
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {misconception.assessmentStrategies.detection_methods.map(
                            (method: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                              >
                                <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                                <div className="prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert flex-1">
                                  <EditableMarkdown
                                    value={method}
                                    isEditable={isEditable ?? true}
                                    handleSave={async (newValue) => {
                                      if (
                                        !misconception.assessmentStrategies
                                          ?.detection_methods
                                      )
                                        return;

                                      const updatedMethods = [
                                        ...misconception.assessmentStrategies
                                          .detection_methods,
                                      ];
                                      updatedMethods[index] = newValue;

                                      await updateAssessmentStrategies(
                                        "detection_methods",
                                        updatedMethods,
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                  {/* Re-evaluation Methods */}
                  {misconception.assessmentStrategies.re_evaluation_methods &&
                    misconception.assessmentStrategies.re_evaluation_methods
                      .length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <BookOpen
                            size={20}
                            className="text-green-600 dark:text-green-400"
                          />
                          <h4 className="font-semibold text-green-600 dark:text-green-400">
                            {tC(
                              contentLanguage,
                              "misconceptions.reEvaluationMethods",
                            )}
                          </h4>
                        </div>
                        <div className="space-y-2">
                          {misconception.assessmentStrategies.re_evaluation_methods.map(
                            (method: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
                              >
                                <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                                <div className="prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert flex-1">
                                  <EditableMarkdown
                                    value={method}
                                    isEditable={isEditable ?? true}
                                    handleSave={async (newValue) => {
                                      if (
                                        !misconception.assessmentStrategies
                                          ?.re_evaluation_methods
                                      )
                                        return;

                                      const updatedMethods = [
                                        ...misconception.assessmentStrategies
                                          .re_evaluation_methods,
                                      ];
                                      updatedMethods[index] = newValue;

                                      await updateAssessmentStrategies(
                                        "re_evaluation_methods",
                                        updatedMethods,
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

          {misconception.misconceptions?.map(
            (item: MisconceptionItem, idx: number) => (
              <div
                key={idx}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50"
              >
                <div className="space-y-8">
                  {item.misconception && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                          <AlertCircle size={20} />
                        </div>
                        <h4 className="font-semibold text-red-600 dark:text-red-400">
                          {tC(
                            contentLanguage,
                            "misconceptions.commonMisconception",
                          )}
                        </h4>
                      </div>
                      <Text variant="p" className="leading-relaxed">
                        <EditableMarkdown
                          value={item.misconception}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!misconception.misconceptions) return;

                            const updatedMisconceptions: MisconceptionItem[] = [
                              ...misconception.misconceptions,
                            ];
                            updatedMisconceptions[idx] = {
                              ...updatedMisconceptions[idx],
                              misconception: newValue,
                            };

                            const updateData: CommonMisconceptionUpdateRequestDto =
                              {
                                misconceptions: updatedMisconceptions,
                              };

                            await updateCommonMisconception(
                              misconception.id!,
                              updateData,
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCommonMisconceptionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCommonMisconceptionQueryKey(
                                misconception.id!,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  )}

                  {item.clarification && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                          <CheckCircle size={20} />
                        </div>
                        <h4 className="font-semibold text-green-600 dark:text-green-400">
                          {tC(contentLanguage, "misconceptions.clarification")}
                        </h4>
                      </div>
                      <Text variant="p" className="leading-relaxed">
                        <EditableMarkdown
                          value={item.clarification}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!misconception.misconceptions) return;

                            const updatedMisconceptions: MisconceptionItem[] = [
                              ...misconception.misconceptions,
                            ];
                            updatedMisconceptions[idx] = {
                              ...updatedMisconceptions[idx],
                              clarification: newValue,
                            };

                            const updateData: CommonMisconceptionUpdateRequestDto =
                              {
                                misconceptions: updatedMisconceptions,
                              };

                            await updateCommonMisconception(
                              misconception.id!,
                              updateData,
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCommonMisconceptionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCommonMisconceptionQueryKey(
                                misconception.id!,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  )}

                  {item.why_misconception_occurs && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                          <Brain size={20} />
                        </div>
                        <h4 className="font-semibold text-blue-600 dark:text-blue-400">
                          {tC(contentLanguage, "misconceptions.whyOccurs")}
                        </h4>
                      </div>
                      <Text variant="p" className="leading-relaxed">
                        <EditableMarkdown
                          value={item.why_misconception_occurs}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!misconception.misconceptions) return;

                            const updatedMisconceptions: MisconceptionItem[] = [
                              ...misconception.misconceptions,
                            ];
                            updatedMisconceptions[idx] = {
                              ...updatedMisconceptions[idx],
                              why_misconception_occurs: newValue,
                            };

                            const updateData: CommonMisconceptionUpdateRequestDto =
                              {
                                misconceptions: updatedMisconceptions,
                              };

                            await updateCommonMisconception(
                              misconception.id!,
                              updateData,
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCommonMisconceptionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCommonMisconceptionQueryKey(
                                misconception.id!,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  )}

                  {item.fix_strategies && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                          <Lightbulb size={20} />
                        </div>
                        <h4 className="font-semibold text-purple-600 dark:text-purple-400">
                          {tC(contentLanguage, "misconceptions.fixStrategies")}
                        </h4>
                      </div>
                      <Text variant="p" className="leading-relaxed">
                        <EditableMarkdown
                          value={item.fix_strategies}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!misconception.misconceptions) return;

                            const updatedMisconceptions: MisconceptionItem[] = [
                              ...misconception.misconceptions,
                            ];
                            updatedMisconceptions[idx] = {
                              ...updatedMisconceptions[idx],
                              fix_strategies: newValue,
                            };

                            const updateData: CommonMisconceptionUpdateRequestDto =
                              {
                                misconceptions: updatedMisconceptions,
                              };

                            await updateCommonMisconception(
                              misconception.id!,
                              updateData,
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCommonMisconceptionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCommonMisconceptionQueryKey(
                                misconception.id!,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  )}

                  {item.real_classroom_example && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                          <GraduationCap size={20} />
                        </div>
                        <h4 className="font-semibold text-amber-600 dark:text-amber-400">
                          {tC(
                            contentLanguage,
                            "misconceptions.connectionsExample",
                          )}
                        </h4>
                      </div>
                      <Text variant="p" className="leading-relaxed">
                        <EditableMarkdown
                          value={item.real_classroom_example}
                          isEditable={isEditable ?? true}
                          handleSave={async (newValue) => {
                            if (!misconception.misconceptions) return;

                            const updatedMisconceptions: MisconceptionItem[] = [
                              ...misconception.misconceptions,
                            ];
                            updatedMisconceptions[idx] = {
                              ...updatedMisconceptions[idx],
                              real_classroom_example: newValue,
                            };

                            const updateData: CommonMisconceptionUpdateRequestDto =
                              {
                                misconceptions: updatedMisconceptions,
                              };

                            await updateCommonMisconception(
                              misconception.id!,
                              updateData,
                            );

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserCommonMisconceptionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetCommonMisconceptionQueryKey(
                                misconception.id!,
                              ),
                            });
                          }}
                        />
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <MisconceptionCardPDF
            customizationSettings={customizationSettings}
            misconception={{ ...misconception, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};

export default MisconceptionCard;
